// src/components/cards/BeaconchainCard.tsx
import React from 'react';
import { BeaconchainData } from '../../types';
import DataStatusIndicator, { DataStatus } from '../DataStatusIndicator';

interface BeaconchainCardProps {
  data: BeaconchainData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
  formatTimestamp: (timestamp: number) => string;
  dataStatus?: DataStatus;
}

const BeaconchainCard: React.FC<BeaconchainCardProps> = ({
  data,
  loading,
  error,
  lastUpdated,
  formatTimestamp,
  dataStatus = 'live'
}) => {
  // Format wait time to hours and days
  const formatWaitTime = (hours: number): string => {
    if (hours < 24) {
      return `${hours.toFixed(1)} hours`;
    } else {
      const days = hours / 24;
      return `${days.toFixed(1)} days`;
    }
  };

  // Get color based on queue length
  const getQueueColor = (queueLength: number): string => {
    if (queueLength > 50000) return 'var(--color-bull)'; // Long queue is bullish (people unstaking)
    if (queueLength > 20000) return 'var(--color-neutral-sentiment)';
    return 'var(--color-bear)'; // Short queue is bearish (people keeping ETH staked)
  };

  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-5 dashboard-card relative">
      <DataStatusIndicator status={dataStatus} lastUpdated={lastUpdated} />
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">Beaconchain Unstaking Queue</h3>
        {loading && (
          <div className="animate-pulse px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">
            Updating...
          </div>
        )}
      </div>

      {error ? (
        <div className="text-red-500 py-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      ) : data ? (
        <>
          {/* Queue Length */}
          <div className="text-3xl font-bold mb-2">
            {data.unstakingQueueLength.toLocaleString()}
          </div>
          <div className="text-sm text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-4">
            validators in unstaking queue
          </div>

          {/* Estimated Wait Time */}
          <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-3 mb-4">
            <div className="text-sm text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
              Estimated Wait Time
            </div>
            <div className="text-xl font-medium">
              {formatWaitTime(data.estimatedWaitTimeHours)}
            </div>
          </div>

          {/* Queue Visualization */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Queue Status</span>
              <span 
                className="font-medium"
                style={{ color: getQueueColor(data.unstakingQueueLength) }}
              >
                {data.unstakingQueueLength > 50000 ? 'High' : 
                 data.unstakingQueueLength > 20000 ? 'Medium' : 'Low'}
              </span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full" 
                style={{
                  width: `${Math.min(data.unstakingQueueLength / 100000 * 100, 100)}%`,
                  backgroundColor: getQueueColor(data.unstakingQueueLength)
                }}
              />
            </div>
          </div>

          {/* Additional Stats */}
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded">
              <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                Total ETH Staked
              </div>
              <div className="text-sm font-medium">
                {(data.totalEthStaked).toLocaleString()} ETH
              </div>
            </div>
            <div className="bg-gray-100 dark:bg-gray-800 p-2 rounded">
              <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
                Active Validators
              </div>
              <div className="text-sm font-medium">
                {data.activeValidators.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Last Updated */}
          <div className="mt-2 text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
            Last updated: {formatTimestamp(lastUpdated || data.timestamp)}
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No Beaconchain data available</p>
        </div>
      )}
    </div>
  );
};

export default BeaconchainCard;
